import { Box, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import { addYears, format } from 'date-fns';
import { useFormik } from 'formik';
import React, { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { DateFormatsEnum } from '../../../constants/date';
import { EXPIRATION_DATE } from '../../../constants/shipping';
import { IOneLinkChunkFormProps } from '../../../contexts/OneLinkSummaryProvider';
import { selectUsers } from '../../../store/selectors/users';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { formatDate, isValidDate } from '../../../utils/date';
import { getFullName, isObjectsEqual } from '../../../utils/helpers';
import { getOneLinkStatusAndDate } from '../../../utils/oneLink';
import { ReportsStatus } from '../../index';
import { FormControls, Label } from '../../SummaryComponentMUI/components/common';
import { DateAndTimezonePicker, UserOptionMUI } from '../inputs';

const SummaryForm = forwardRef<boolean | null, IOneLinkChunkFormProps>(({ onSubmit, instance, onClose }, ref) => {
  const { sass } = useTheme();
  const users = useSelector(selectUsers);

  const validationSchema = yup.object().shape({
    expiration_date: yup.string().nullable(),
    title: yup.string().notRequired(),
    user_id: yup.string().required('Sender is required'),
  });

  const formik = useFormik({
    initialValues: {
      title: instance?.title || '',
      user_id: instance?.user_id || '',
      expiration_date:
        instance?.expiration_date && isValidDate(new Date(instance.expiration_date)) ? instance.expiration_date : null,
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit({ summary_info: values });
      if (ref && typeof ref !== 'function') {
        ref.current = false;
      }
    },
  });

  const { user_id, expiration_date, title } = formik.values;
  const { handleChange, setFieldValue, handleSubmit, initialValues } = formik;

  const { label: statusLabel, color: statusColor } = React.useMemo(() => {
    if (instance) {
      return getOneLinkStatusAndDate(instance as IOneLinkWithCampaignSummaries);
    }
    return { label: '', color: '' };
  }, [instance]);

  const status = useMemo(
    () => <ReportsStatus status={statusLabel} backgroundColor={statusColor} />,
    [statusColor, statusLabel],
  );

  const isSenderAccessible = useMemo(
    () => users?.some((user) => user.uid === instance?.user_id),
    [users, instance?.user_id],
  );

  const allUsers = useMemo(() => {
    return isSenderAccessible
      ? users
      : [
          ...(users ?? []),
          {
            uid: instance?.user_id ?? '',
            first_name: instance?.sender_first_name ?? 'Unknown',
            last_name: instance?.sender_last_name ?? 'User',
          },
        ];
  }, [isSenderAccessible, instance?.sender_first_name, instance?.sender_last_name, instance?.user_id, users]);

  const handleClose = useCallback(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = false;
    }
    onClose();
  }, [ref, onClose]);

  useEffect(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = !isObjectsEqual(initialValues, formik.values);
    }
  }, [formik.values, initialValues]);

  return (
    <Box
      component="form"
      role="form"
      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
      onSubmit={handleSubmit}
    >
      <Box>
        <Box sx={{ mb: 4 }}>
          <Label>
            <Typography component="span" sx={{ mb: 2 }}>
              Status
            </Typography>
            <Typography component="span" sx={{ color: sass.ebony }}>
              {status}
            </Typography>
          </Label>
        </Box>
        <TextField
          label="Title"
          name="title"
          value={title}
          onChange={handleChange}
          variant="outlined"
          fullWidth
          sx={{ mb: 4 }}
        />
        <TextField
          label="Sender"
          name="user_id"
          value={user_id}
          onChange={handleChange}
          variant="outlined"
          select
          fullWidth
          sx={{ mb: 4 }}
          slotProps={{
            select: {
              renderValue: (selected) => {
                const selectedUser = allUsers?.find((user) => user.uid === selected);
                return selectedUser ? getFullName(selectedUser.first_name, selectedUser.last_name) : '';
              },
              MenuProps: {
                sx: {
                  '& .MuiPaper-root': {
                    maxHeight: 260,
                    overflowY: 'auto',
                  },
                },
              },
            },
          }}
        >
          {allUsers?.map((user) => (
            <MenuItem
              key={user.uid}
              value={user.uid}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <UserOptionMUI user={user} />
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Label>Expiration Date</Label>
          <DateAndTimezonePicker
            value={expiration_date}
            maxDate={addYears(new Date(), 1)}
            onSubmit={(newStartDate) => {
              setFieldValue(
                EXPIRATION_DATE,
                newStartDate
                  ? formatDate(
                      newStartDate.selectedDate
                        ? format(newStartDate.selectedDate, DateFormatsEnum.FullDateAndTime)
                        : '',
                      DateFormatsEnum.FullDateAndTime,
                    )
                  : null,
              );
            }}
            showLabel={false}
            isClearable
          />
        </Box>
      </Box>
      <FormControls
        onCancel={handleClose}
        isDisabled={!formik.isValid || (ref && typeof ref !== 'function' ? !ref.current : true)}
      />
    </Box>
  );
});

export default SummaryForm;
